import { take, put, all, spawn } from "redux-saga/effects";

import { SET_SESSION_DATA, CREATE_SESSION } from "../../actions/sessionActions";
import {
    StringMap,
    SessionInitData,
    SdsAccountInfo,
    BopDisabledFlagMode,
    BumpItem,
} from "../../../api/generated/models";

import client from "../../../api/client/SdsWebApiClient";
import {
    getAppVersion,
    getFrontendSettings,
    loadAppCustomization,
    loadConfigurationFiles,
} from "../../../utilities/configUtils";
import Constants from "../../../constants";
import { loadProductInfoSaga } from "./metadataSaga";
import { SummaryConfig } from "../../../models/summaryConfig";
import TranslationMap from "../../../models/translationMap";
import { setFilterTypes } from "./filterTypeSaga";
import { loadConfigurationSaga } from "./configurationSaga";

export function* createSessionSaga(): any {
    const { initialConfig, configEncoding, surfaceProductArticleNo } = yield take(CREATE_SESSION);
    if (!initialConfig) {
        throw new Error("initialConfig is not set");
    }

    const clientSettingsPromise = client.getClientSettingsGet();
    const frontendSettingsPromise = getFrontendSettings();
    const appVersionPromise = getAppVersion();

    const searchParams = new URLSearchParams(window.location.search);
    const accountId = searchParams.get(Constants.accountUrlQueryParam);
    let market = searchParams.get(Constants.marketUrlQueryParam) || undefined;
    let language = searchParams.get(Constants.languageUrlQueryParam) || (yield client.getDefaultLanguageGet());
    language = language.toUpperCase();

    const sessionData: SessionInitData = yield client.createSessionPost(
        undefined,
        language,
        market,
        BopDisabledFlagMode.IgnoreDisabledFlag
    );

    let account: SdsAccountInfo | null = null;
    if (accountId) {
        account = yield client.setCurrentSdsAccountPost(sessionData.sessionId, accountId);
    }

    if (!account) {
        account = yield client.getCurrentSdsAccountGet(sessionData.sessionId);
    }

    const configsPromise = loadConfigurationFiles(language, account?.id);

    const [, [appConfig, summaryData], applicationSettings, frontendSettings, appVersion]: [
        any,
        [Object, SummaryConfig],
        StringMap,
        any,
        string
    ] = yield all([
        loadProductInfoSaga(language, sessionData.localization, account?.id),
        configsPromise,
        clientSettingsPromise,
        frontendSettingsPromise,
        appVersionPromise,
    ]);

    // eslint-disable-next-line no-console
    console.log(`Configurator version: ${appVersion}`);

    let showPrice = applicationSettings.dict.find(x => x.key === "price.enable")?.value === "True";
    const hidePrice = searchParams.get("hide_price");
    if (
        (hidePrice && /true/i.test(hidePrice)) ||
        frontendSettings.hide_price === true ||
        /true/i.test(frontendSettings.hide_price)
    ) {
        showPrice = false;
    }

    let showFilters = frontendSettings.show_filters;
    const hideFilters = searchParams.get("show_filters");

    if (
        (hideFilters && /false/i.test(hideFilters)) ||
        frontendSettings.show_filters === false ||
        /false/i.test(frontendSettings.show_filters)
    ) {
        showFilters = false;
    } else if (showFilters === undefined && hideFilters && /true/i.test(hideFilters)) {
        showFilters = true;
    }

    let touchScreen = false;
    const isTouchScreen = searchParams.get("touch_screen");

    if (isTouchScreen && /true/i.test(isTouchScreen)) {
        touchScreen = true;
    }

    /* eslint-disable no-param-reassign */
    const guiTranslation = sessionData.bumps[0].items.reduce(
        (translations: { [key: string]: string }, item: BumpItem) => {
            if (item.shortText) translations[item.id] = item.shortText;
            return translations;
        },
        {}
    );

    const appCustomization = account ? yield loadAppCustomization(account) : null;
    if (showFilters) {
        yield setFilterTypes(frontendSettings, appCustomization);
    }

    yield put({
        type: SET_SESSION_DATA,
        sessionData,
        frontendSettings,
        appConfig,
        summaryData,
        applicationSettings,
        language,
        showPrice,
        account,
        showFilters,
        guiTranslation: new TranslationMap(guiTranslation),
        appVersion,
        touchScreen,
        appCustomization,
    });

    yield spawn(loadConfigurationSaga, initialConfig, configEncoding, surfaceProductArticleNo);
}
